import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  FileText,
  Filter,
  Flag,
  Gift,
  HeartPulse,
  Image,
  LineChart,
  Lock,
  Mail,
  Network,
  Package,
  Plug,
  RefreshCw,
  Repeat2,
  ServerCog,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  Users,
  Video,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { getClerkPublishableKey, isLikelyClerkPublishableKey } from "@/lib/clerkPublishableKey";
import { useIsSuperAdmin } from "@/lib/auth";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import {
  AdminEmptyState,
  AdminGlassCard,
  AdminKpiCard,
  AdminPanel,
  AdminSectionLabel,
} from "@/components/admin/AdminGlass";
import { HeroCtaSummaryCard } from "@/components/admin/HeroCtaSummaryCard";
import { useAdminDashboardOverview } from "@/hooks/useAdminDashboardOverview";
import { ADMIN_PAGE_CATALOG as C } from "@/lib/adminPageCatalog";
import {
  formatUsdCents,
  gatewayMoneyLabel,
} from "@/lib/adminMoneyFormat";

// ─── Local nav card (dashboard-only shortcut grid) ────────────────────────────

function NavCard({
  to,
  icon: Icon,
  iconColor,
  title,
  description,
  label = "Ir",
  superAdminOnly = false,
  external = false,
}: {
  to: string;
  icon: React.ElementType;
  iconColor: string;
  title: string;
  description: string;
  label?: string;
  superAdminOnly?: boolean;
  external?: boolean;
}) {
  const inner = (
    <AdminGlassCard className="group relative h-full cursor-pointer overflow-hidden p-4 transition-all duration-200 hover:border-white/12 hover:bg-white/[0.055]">
      <div className="flex flex-col gap-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="inline-flex items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.04] p-2">
            <Icon className={cn("h-4 w-4", iconColor)} strokeWidth={1.75} />
          </div>
          {superAdminOnly && (
            <span className="flex items-center gap-1 rounded-full border border-white/[0.09] bg-white/[0.05] px-2 py-0.5 text-[10px] font-medium text-white/45">
              <Lock className="h-2.5 w-2.5" />
              Super
            </span>
          )}
        </div>
        <div>
          <p className="text-sm font-semibold text-white/85">{title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-white/40">{description}</p>
        </div>
        <div className="flex items-center gap-1 text-xs text-white/35 transition-colors group-hover:text-white/65">
          <span>{label}</span>
          {external ? (
            <ExternalLink className="h-3 w-3" />
          ) : (
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          )}
        </div>
      </div>
    </AdminGlassCard>
  );

  return external ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className="block h-full">
      {inner}
    </a>
  ) : (
    <Link to={to} className="block h-full">
      {inner}
    </Link>
  );
}

function formatUsd(cents: number): string {
  return formatUsdCents(cents, { whole: true });
}

function formatDwell(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

function timeAgo(iso: string, nowMs: number): string {
  try {
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return "—";
    const sec = Math.max(0, Math.floor((nowMs - t) / 1000));
    if (sec < 10) return "ahora";
    if (sec < 60) return `hace ${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `hace ${min}m`;
    return `hace ${Math.floor(min / 60)}h`;
  } catch {
    return "—";
  }
}

function maskEmail(email: string | null): string | null {
  if (!email || !email.includes("@")) return null;
  const [u, d] = email.split("@");
  if (!d) return null;
  const head = u.slice(0, 2);
  return `${head}…@${d}`;
}

function shortEvent(name: string): string {
  return name
    .replace(/^membership_email_/, "email_")
    .replace(/^vip_membership_/, "vip_")
    .replace(/^dual_cta_/, "dual_")
    .replace(/^exclusive_ar_invite_/, "inv_");
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const clerkPk = getClerkPublishableKey();
  const clerkKeyOk = Boolean(clerkPk && isLikelyClerkPublishableKey(clerkPk));
  const isSuperAdmin = useIsSuperAdmin();
  const { getAuthHeaders } = useAdminAuth();

  const { data, loading, error, updatedAt, refresh } =
    useAdminDashboardOverview(isSuperAdmin);

  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 5000);
    return () => window.clearInterval(id);
  }, []);

  const ageLabel = useMemo(() => {
    if (!updatedAt) return loading ? "Cargando…" : "Sin datos";
    const sec = Math.max(0, Math.floor((nowTick - updatedAt) / 1000));
    if (sec < 5) return "Actualizado ahora";
    if (sec < 60) return `Actualizado hace ${sec}s`;
    return `Actualizado hace ${Math.floor(sec / 60)}m`;
  }, [updatedAt, nowTick, loading]);

  const [reportSending, setReportSending] = useState(false);
  const [reportHours, setReportHours] = useState(24);
  const [reportNotice, setReportNotice] = useState<{
    kind: "ok" | "err";
    message: string;
  } | null>(null);

  async function sendMetricsReportEmail() {
    setReportSending(true);
    setReportNotice(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/send-metrics-report", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ hours: reportHours }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        blockers?: string[];
        recipient_preview?: string;
        recipients_count?: number;
      };
      if (!res.ok) {
        const detail =
          Array.isArray(body.blockers) && body.blockers.length > 0
            ? body.blockers.join(" · ")
            : body.error || `HTTP ${res.status}`;
        throw new Error(detail);
      }
      setReportNotice({
        kind: "ok",
        message: `Reporte enviado a ${body.recipients_count ?? "?"} destinatario(s): ${body.recipient_preview ?? ""}`,
      });
    } catch (e) {
      setReportNotice({
        kind: "err",
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setReportSending(false);
    }
  }

  const money = data?.money;
  const live = data?.live;
  const leads = data?.leads;
  const photos = data?.photos;
  const memberships = data?.memberships;
  const buyers = data?.buyers;

  return (
    <AdminPageShell width="xl">
      <AdminPageHeader
        description={
          isSuperAdmin
            ? `Operación del sitio (producto): live, emails, fotos y ${gatewayMoneyLabel("stripe")}. ${ageLabel}. Revenue de producto en /admin/revenue — no es finanzas personales.`
            : "Panel de administración"
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() => refresh()}
                className="flex items-center gap-1.5 rounded-xl border border-white/[0.09] bg-white/[0.05] px-3 py-2 text-xs font-medium text-white/60 transition-all hover:bg-white/[0.09] hover:text-white/90"
              >
                <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                Actualizar
              </button>
            )}
            <Link
              to="/"
              target="_blank"
              className="flex items-center gap-1.5 rounded-xl border border-white/[0.09] bg-white/[0.05] px-3 py-2 text-xs font-medium text-white/60 transition-all hover:bg-white/[0.09] hover:text-white/90"
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
              Ver sitio
            </Link>
            {isSuperAdmin && (
              <>
                <Link
                  to={C.buyers.path}
                  className="flex items-center gap-1.5 rounded-xl border border-white/[0.09] bg-white/[0.06] px-3 py-2 text-xs font-medium text-white/70 transition-all hover:bg-white/[0.1] hover:text-white"
                >
                  <Users className="h-3.5 w-3.5" />
                  Compradores
                </Link>
                <Link
                  to={C["kimi-patterns"].path}
                  className="flex items-center gap-1.5 rounded-xl border border-white/[0.09] bg-white/[0.06] px-3 py-2 text-xs font-medium text-white/70 transition-all hover:bg-white/[0.1] hover:text-white"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Offer
                </Link>
                <Link
                  to={C["live-funnel"].path}
                  className="flex items-center gap-1.5 rounded-xl border border-white/[0.09] bg-white/[0.06] px-3 py-2 text-xs font-medium text-white/70 transition-all hover:bg-white/[0.1] hover:text-white"
                >
                  <Activity className="h-3.5 w-3.5" />
                  Live
                </Link>
                <Link
                  to={C.revenue.path}
                  className="flex items-center gap-1.5 rounded-xl border border-white/[0.09] bg-white/[0.06] px-3 py-2 text-xs font-medium text-white/70 transition-all hover:bg-white/[0.1] hover:text-white"
                >
                  <LineChart className="h-3.5 w-3.5" />
                  Revenue
                </Link>
              </>
            )}
          </div>
        }
      />

      {isSuperAdmin && error && (
        <Alert className="border-red-400/30 bg-red-500/10">
          <AlertTriangle className="h-4 w-4 text-red-300" />
          <AlertTitle className="text-white/90">No se pudo cargar el overview</AlertTitle>
          <AlertDescription className="text-white/55">
            {error}{" "}
            <button
              type="button"
              className="underline hover:text-white/85"
              onClick={() => refresh()}
            >
              Reintentar
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* ── KPIs reales ── */}
      {isSuperAdmin && (
        <div>
          <AdminSectionLabel
            icon={TrendingUp}
            title="Resumen operativo"
            description={`Producto / brand · ${gatewayMoneyLabel("stripe")} · compradores · pending · live · leads · fotos`}
          />
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8">
            <AdminKpiCard
              icon={CreditCard}
              iconColor="text-amber-300"
              glowColor="bg-amber-500/15"
              accent="stripe"
              label="Revenue 7d"
              value={formatUsd(money?.revenue_7d_cents ?? 0)}
              hint={gatewayMoneyLabel("stripe")}
              loading={loading && !data}
              to="/admin/revenue"
            />
            <AdminKpiCard
              icon={TrendingUp}
              iconColor="text-white/70"
              glowColor="bg-white/10"
              label="Tx 7d"
              value={(money?.tx_7d ?? 0).toLocaleString("es-AR")}
              hint="Pagos succeeded · Stripe"
              loading={loading && !data}
              to="/admin/revenue"
            />
            <AdminKpiCard
              icon={Users}
              iconColor="text-emerald-300"
              glowColor="bg-emerald-500/15"
              accent="stripe"
              label="Stripe 48h"
              value={(buyers?.stripe_paid_2d ?? 0).toLocaleString("es-AR")}
              hint={
                buyers?.stripe_ok === false
                  ? buyers?.stripe_error || "Stripe err"
                  : formatUsd(buyers?.stripe_paid_2d_cents ?? 0)
              }
              loading={loading && !data}
              to="/admin/buyers"
            />
            <AdminKpiCard
              icon={Users}
              iconColor="text-white/70"
              glowColor="bg-white/10"
              label="Membresías 7d"
              value={(
                buyers?.memberships_7d ??
                memberships?.brand_active_7d ??
                0
              ).toLocaleString("es-AR")}
              hint="user_memberships brand"
              loading={loading && !data}
              to="/admin/buyers"
            />
            <AdminKpiCard
              icon={Shield}
              iconColor="text-amber-200"
              glowColor="bg-amber-500/15"
              label="Pending"
              value={(memberships?.pending_approval ?? 0).toLocaleString("es-AR")}
              hint="Aprobar invite"
              loading={loading && !data}
              to="/admin/memberships"
            />
            <AdminKpiCard
              icon={Mail}
              iconColor="text-sky-300"
              glowColor="bg-sky-500/15"
              label="Emails 24h"
              value={(leads?.emails_captured_24h ?? 0).toLocaleString("es-AR")}
              hint={`${leads?.with_coupon_promo50_24h ?? 0} con ANA70`}
              loading={loading && !data}
              to="/admin/recovery-leads"
            />
            <AdminKpiCard
              icon={Activity}
              iconColor="text-emerald-300"
              glowColor="bg-emerald-500/15"
              label="Live"
              value={(live?.active_sessions ?? 0).toLocaleString("es-AR")}
              hint={`${live?.events_last_5m ?? 0} eventos · 5m`}
              loading={loading && !data}
              to="/admin/live-funnel"
            />
            <AdminKpiCard
              icon={Image}
              iconColor="text-violet-300"
              glowColor="bg-violet-500/15"
              label="Fotos 24h"
              value={(photos?.views_24h ?? 0).toLocaleString("es-AR")}
              hint={`${photos?.sessions_24h ?? 0} sesiones`}
              loading={loading && !data}
              to="/admin/image-metrics"
            />
          </div>
        </div>
      )}

      {/* ── Compradores recientes (DB + Stripe live) ── */}
      {isSuperAdmin && (
        <AdminPanel
          icon={Users}
          title="Compradores recientes · este brand"
          description={`${gatewayMoneyLabel("stripe")} · montos en USD cuando aplica`}
          action={
            <div className="flex flex-wrap gap-3 text-[11px]">
              <Link to="/admin/buyers" className="text-white/45 hover:text-white/75">
                Compradores →
              </Link>
              <Link to="/admin/kimi-patterns" className="text-white/45 hover:text-white/75">
                Offer engine →
              </Link>
              <Link to="/admin/memberships" className="text-white/45 hover:text-white/75">
                Aprobaciones →
              </Link>
            </div>
          }
        >
          {loading && !data ? (
            <Skeleton className="h-16 w-full rounded-xl bg-white/[0.06]" />
          ) : (buyers?.latest?.length ?? 0) === 0 ? (
            <AdminEmptyState>
              Sin compradores brand en la ventana. Usá Reconciliar en{" "}
              <Link to="/admin/buyers" className="underline hover:text-white/70">
                /admin/buyers
              </Link>
              .
            </AdminEmptyState>
          ) : (
            <ul className="divide-y divide-white/[0.05]">
              {(buyers?.latest || []).slice(0, 6).map((b) => (
                <li
                  key={`${b.email}-${b.started_at || ""}-${b.data_source}`}
                  className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-white/80">{b.email}</p>
                    <p className="text-[11px] text-white/30">
                      {[b.country, b.flow_name, b.data_source === "stripe_checkout_session" ? "Stripe" : "DB"]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <div className="text-right tabular-nums text-white/70">
                    {b.amount != null
                      ? formatUsd(
                          (b.currency || "usd").toLowerCase() === "usd"
                            ? b.amount
                            : b.amount,
                        )
                      : "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </AdminPanel>
      )}

      {/* ── Pulse: live + leads ── */}
      {isSuperAdmin && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <AdminGlassCard className="p-4 sm:p-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-emerald-400/80" />
                <p className="text-sm font-semibold text-white/70">Ahora · funnel</p>
              </div>
              <Link
                to="/admin/live-funnel"
                className="text-[11px] text-white/30 hover:text-white/60"
              >
                Ver live →
              </Link>
            </div>
            {loading && !data ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-9 w-full rounded-lg bg-white/[0.05]" />
                ))}
              </div>
            ) : (live?.latest?.length ?? 0) === 0 ? (
              <AdminEmptyState className="min-h-[72px] py-5">
                Sin actividad en los últimos 5 minutos
              </AdminEmptyState>
            ) : (
              <ul className="space-y-1.5">
                {live!.latest.map((ev, i) => (
                  <li
                    key={`${ev.created_at}-${ev.event_name}-${i}`}
                    className="flex items-start justify-between gap-2 rounded-lg border border-white/[0.04] bg-white/[0.02] px-2.5 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-mono text-[11px] text-white/70">
                        {shortEvent(ev.event_name)}
                      </p>
                      <p className="mt-0.5 text-[10px] text-white/30">
                        {[ev.country, ev.city].filter(Boolean).join(" · ") || "—"}
                        {maskEmail(ev.customer_email)
                          ? ` · ${maskEmail(ev.customer_email)}`
                          : ""}
                      </p>
                    </div>
                    <span className="shrink-0 text-[10px] tabular-nums text-white/40">
                      {timeAgo(ev.created_at, nowTick)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {(live?.top_events?.length ?? 0) > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5 border-t border-white/[0.05] pt-3">
                {live!.top_events.map((t) => (
                  <span
                    key={t.event_name}
                    className="rounded-full border border-white/[0.06] bg-white/[0.03] px-2 py-0.5 text-[10px] text-white/40"
                  >
                    {shortEvent(t.event_name)}{" "}
                    <span className="tabular-nums text-white/60">{t.count}</span>
                  </span>
                ))}
              </div>
            )}
          </AdminGlassCard>

          <AdminGlassCard className="p-4 sm:p-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-sky-400/80" />
                <p className="text-sm font-semibold text-white/70">Leads & recovery</p>
              </div>
              <Link
                to="/admin/recovery-leads"
                className="text-[11px] text-white/30 hover:text-white/60"
              >
                Ver leads →
              </Link>
            </div>
            {loading && !data ? (
              <div className="grid grid-cols-2 gap-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-16 rounded-xl bg-white/[0.05]" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    label: "Emails 24h",
                    value: leads?.emails_captured_24h ?? 0,
                    hint: "Capturados (gate / intents)",
                  },
                  {
                    label: "ANA70 / gate",
                    value: leads?.with_coupon_promo50_24h ?? 0,
                    hint: "Con oferta 70% OFF (ANA70)",
                  },
                  {
                    label: "Recoverable",
                    value: leads?.recoverable_2_24h ?? 0,
                    hint: "Ventana 2–24h sin mail",
                  },
                  {
                    label: "Completed 24h",
                    value: leads?.completed_24h ?? 0,
                    hint: "Intents completed",
                  },
                ].map((c) => (
                  <div
                    key={c.label}
                    className="rounded-xl border border-white/[0.05] bg-white/[0.02] px-3 py-2.5"
                  >
                    <p className="text-[10px] uppercase tracking-wide text-white/45">
                      {c.label}
                    </p>
                    <p className="mt-0.5 text-xl font-semibold tabular-nums text-white/85">
                      {c.value.toLocaleString()}
                    </p>
                    <p className="mt-0.5 text-[10px] text-white/40">{c.hint}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/[0.05] pt-3 text-[11px] text-white/45">
              <span>
                Membresías pendientes:{" "}
                <strong className="tabular-nums text-white/70">
                  {(memberships?.pending_approval ?? 0).toLocaleString()}
                </strong>
              </span>
              <span className="text-white/40">·</span>
              <span>
                Stripe brand 48h:{" "}
                <strong className="tabular-nums text-white/70">
                  {(buyers?.stripe_paid_2d ?? 0).toLocaleString()}
                </strong>
              </span>
              <Link
                to="/admin/memberships"
                className="ml-auto text-white/40 underline-offset-2 hover:text-white/70 hover:underline"
              >
                Aprobar →
              </Link>
            </div>
          </AdminGlassCard>
        </div>
      )}

      {/* ── Fotos 24h ── */}
      {isSuperAdmin && (
        <AdminGlassCard className="p-4 sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Image className="h-4 w-4 text-violet-400/80" />
              <p className="text-sm font-semibold text-white/70">Engagement fotos · 24h</p>
            </div>
            <Link
              to="/admin/image-metrics"
              className="text-[11px] text-white/30 hover:text-white/60"
            >
              Métricas →
            </Link>
          </div>
          {loading && !data ? (
            <Skeleton className="h-12 w-full rounded-xl bg-white/[0.05]" />
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-4 text-sm">
                <div>
                  <p className="text-[10px] uppercase text-white/45">Vistas</p>
                  <p className="tabular-nums font-semibold text-white/85">
                    {(photos?.views_24h ?? 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-white/45">Sesiones</p>
                  <p className="tabular-nums font-semibold text-white/85">
                    {(photos?.sessions_24h ?? 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-white/45">Tiempo total</p>
                  <p className="tabular-nums font-semibold text-white/85">
                    {formatDwell(photos?.total_dwell_ms_24h ?? 0)}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(photos?.top_countries ?? []).length === 0 ? (
                  <span className="text-xs text-white/40">Sin datos de país</span>
                ) : (
                  photos!.top_countries.map((c) => (
                    <span
                      key={c.country}
                      className="rounded-full border border-white/[0.06] bg-white/[0.03] px-2.5 py-1 text-[11px] text-white/45"
                    >
                      {c.country}{" "}
                      <span className="tabular-nums text-white/70">{c.views}</span>
                    </span>
                  ))
                )}
              </div>
            </div>
          )}
        </AdminGlassCard>
      )}

      <HeroCtaSummaryCard />

      {/* Mismo mapa que el sidebar: Conversión → Sitio → Comercio → Operación → Sistema */}
      <div>
        <AdminSectionLabel
          icon={Flag}
          title="Conversión"
          description="Qué ve el visitante en home y CTAs (editar aquí)"
        />
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          <NavCard
            to={C["country-templates"].path}
            icon={Flag}
            iconColor="text-white/60"
            title={C["country-templates"].navLabel}
            description={C["country-templates"].purpose}
          />
          <NavCard
            to={C.wishlist.path}
            icon={Gift}
            iconColor="text-white/60"
            title={C.wishlist.navLabel}
            description={C.wishlist.purpose}
          />
          <NavCard
            to={C.cta.path}
            icon={BookOpen}
            iconColor="text-white/60"
            title={C.cta.navLabel}
            description={C.cta.purpose}
          />
        </div>
      </div>

      <div>
        <AdminSectionLabel icon={FileText} title="Sitio" description="Shell y contenido público" />
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          <NavCard
            to={C["site-settings"].path}
            icon={FileText}
            iconColor="text-white/60"
            title={C["site-settings"].navLabel}
            description={C["site-settings"].purpose}
          />
          <NavCard
            to={C.portfolio.path}
            icon={Image}
            iconColor="text-white/60"
            title={C.portfolio.navLabel}
            description={C.portfolio.purpose}
          />
          <NavCard
            to={C.audiovisual.path}
            icon={Video}
            iconColor="text-white/60"
            title={C.audiovisual.navLabel}
            description={C.audiovisual.purpose}
          />
        </div>
      </div>

      <div>
        <AdminSectionLabel
          icon={CreditCard}
          title="Comercio"
          description="Catálogo, planes, códigos y aprobaciones"
        />
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {isSuperAdmin && (
            <NavCard
              to={C.revenue.path}
              icon={LineChart}
              iconColor="text-white/60"
              title={C.revenue.navLabel}
              description={C.revenue.purpose}
              label="Ver dashboard"
              superAdminOnly
            />
          )}
          <NavCard
            to={C.productos.path}
            icon={Package}
            iconColor="text-white/60"
            title={C.productos.navLabel}
            description={C.productos.purpose}
          />
          {isSuperAdmin && (
            <NavCard
              to={C.suscripciones.path}
              icon={Repeat2}
              iconColor="text-white/60"
              title={C.suscripciones.navLabel}
              description={C.suscripciones.purpose}
              superAdminOnly
            />
          )}
          <NavCard
            to={C.invitaciones.path}
            icon={Mail}
            iconColor="text-white/60"
            title={C.invitaciones.navLabel}
            description={C.invitaciones.purpose}
          />
          {isSuperAdmin && (
            <NavCard
              to={C["premium-invite"].path}
              icon={Gift}
              iconColor="text-white/60"
              title={C["premium-invite"].navLabel}
              description={C["premium-invite"].purpose}
              superAdminOnly
            />
          )}
          {isSuperAdmin && (
            <NavCard
              to={C.memberships.path}
              icon={Shield}
              iconColor="text-amber-300/80"
              title={C.memberships.navLabel}
              description={C.memberships.purpose}
              superAdminOnly
            />
          )}
          {isSuperAdmin && (
            <NavCard
              to={C.buyers.path}
              icon={Users}
              iconColor="text-emerald-400/80"
              title={C.buyers.navLabel}
              description={C.buyers.purpose}
              superAdminOnly
            />
          )}
          <NavCard
            to={C["argen-links"].path}
            icon={Flag}
            iconColor="text-white/60"
            title={C["argen-links"].navLabel}
            description={C["argen-links"].purpose}
          />
        </div>
      </div>

      {isSuperAdmin && (
        <div>
          <AdminSectionLabel
            icon={Activity}
            title="Operación"
            description="Funnel, leads, Offer LLM, fotos, ISP"
          />
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            <NavCard
              to={C["kimi-patterns"].path}
              icon={Sparkles}
              iconColor="text-violet-300/80"
              title={C["kimi-patterns"].navLabel}
              description={C["kimi-patterns"].purpose}
              superAdminOnly
            />
            <NavCard
              to={C["live-funnel"].path}
              icon={Activity}
              iconColor="text-white/60"
              title={C["live-funnel"].navLabel}
              description={C["live-funnel"].purpose}
              superAdminOnly
            />
            <NavCard
              to={C["checkout-funnel"].path}
              icon={Filter}
              iconColor="text-white/60"
              title={C["checkout-funnel"].navLabel}
              description={C["checkout-funnel"].purpose}
              superAdminOnly
            />
            <NavCard
              to={C["recovery-leads"].path}
              icon={Mail}
              iconColor="text-amber-400"
              title={C["recovery-leads"].navLabel}
              description={C["recovery-leads"].purpose}
              superAdminOnly
            />
            <NavCard
              to={C["image-metrics"].path}
              icon={Image}
              iconColor="text-violet-400"
              title={C["image-metrics"].navLabel}
              description={C["image-metrics"].purpose}
              superAdminOnly
            />
            <NavCard
              to={C["isp-report"].path}
              icon={Network}
              iconColor="text-sky-400"
              title={C["isp-report"].navLabel}
              description={C["isp-report"].purpose}
              superAdminOnly
            />
          </div>
        </div>
      )}

      {isSuperAdmin && (
        <div>
          <AdminSectionLabel
            icon={ServerCog}
            title="Sistema"
            description="Diagnóstico, edge y permisos"
          />
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            <NavCard
              to={C.integrations.path}
              icon={Plug}
              iconColor="text-white/60"
              title={C.integrations.navLabel}
              description={C.integrations.purpose}
              superAdminOnly
            />
            <NavCard
              to={C["config-health"].path}
              icon={HeartPulse}
              iconColor="text-white/60"
              title={C["config-health"].navLabel}
              description={C["config-health"].purpose}
              superAdminOnly
            />
            <NavCard
              to={C["worker-flags"].path}
              icon={Zap}
              iconColor="text-white/60"
              title={C["worker-flags"].navLabel}
              description={C["worker-flags"].purpose}
              superAdminOnly
            />
            <NavCard
              to={C["env-setup"].path}
              icon={ServerCog}
              iconColor="text-white/60"
              title={C["env-setup"].navLabel}
              description={C["env-setup"].purpose}
              superAdminOnly
            />
            <NavCard
              to={C["stripe-disputes"].path}
              icon={LineChart}
              iconColor="text-white/60"
              title={C["stripe-disputes"].navLabel}
              description={C["stripe-disputes"].purpose}
              superAdminOnly
            />
            <NavCard
              to={C["sidebar-config"].path}
              icon={SlidersHorizontal}
              iconColor="text-white/60"
              title={C["sidebar-config"].navLabel}
              description={C["sidebar-config"].purpose}
              superAdminOnly
            />
            <NavCard
              to={C.centro.path}
              icon={BookOpen}
              iconColor="text-white/60"
              title={C.centro.navLabel}
              description={C.centro.purpose}
              superAdminOnly
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <AdminGlassCard className="p-4 sm:p-5">
          <div className="mb-3 flex items-center gap-2">
            <ArrowUpRight className="h-4 w-4 text-white/40" />
            <p className="text-sm font-semibold text-white/70">Enlaces rápidos</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { to: "/", label: "Inicio", icon: ArrowRight },
              { to: "/portfolio", label: "Portfolio", icon: Image },
              { to: "/sobre-mi", label: "Sobre mí", icon: FileText },
              { to: "/audiovisual", label: "Audiovisual", icon: Video },
            ].map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                target="_blank"
                className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-xs text-white/40 transition-all hover:bg-white/[0.06] hover:text-white/70"
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                {label}
              </Link>
            ))}
          </div>
        </AdminGlassCard>

        <AdminGlassCard className="p-4 sm:p-5">
          <div className="mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4 text-white/40" />
            <p className="text-sm font-semibold text-white/70">Autenticación Clerk</p>
          </div>
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2">
            <span
              className={cn(
                "h-2 w-2 shrink-0 rounded-full",
                clerkKeyOk ? "bg-emerald-400/70" : clerkPk ? "bg-white/30" : "bg-red-400/60",
              )}
            />
            <span className="text-xs text-white/40">
              {clerkKeyOk
                ? "Publishable key detectada"
                : clerkPk
                  ? "Key con formato dudoso"
                  : "Key no detectada"}
            </span>
          </div>
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-white/50">
              <ShieldCheck className="h-3.5 w-3.5" />
              Super admin
            </p>
            <p className="text-[11px] text-white/40">
              Clerk → Users → Public metadata:{" "}
              <code className="rounded border border-white/[0.08] bg-white/[0.04] px-1 py-0.5 text-[10px] text-white/55">
                {'{ "role": "super_admin" }'}
              </code>
            </p>
          </div>
        </AdminGlassCard>
      </div>

      {isSuperAdmin && (
        <div>
          <AdminSectionLabel icon={Mail} title="Reporte de métricas por email" />
          <AdminGlassCard className="p-4 sm:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-white/45">Ventana:</span>
                {[24, 72, 168].map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setReportHours(h)}
                    className={cn(
                      "rounded-xl border px-3 py-1.5 text-xs font-medium transition-all",
                      reportHours === h
                        ? "border-white/15 bg-white/10 text-white/80"
                        : "border-white/[0.06] bg-white/[0.03] text-white/45 hover:bg-white/[0.06]",
                    )}
                  >
                    {h}h
                  </button>
                ))}
              </div>
              <button
                type="button"
                disabled={reportSending}
                className="rounded-xl border border-white/[0.08] bg-white/[0.05] px-4 py-2 text-sm font-medium text-white/60 transition-all hover:bg-white/[0.09] disabled:opacity-40"
                onClick={() => void sendMetricsReportEmail()}
              >
                {reportSending ? "Enviando…" : "Enviar reporte"}
              </button>
            </div>
            {reportNotice && (
              <div
                className={cn(
                  "mt-3 flex items-start gap-3 rounded-xl border p-3",
                  reportNotice.kind === "ok"
                    ? "border-white/[0.08] bg-white/[0.04]"
                    : "border-red-500/20 bg-red-500/5",
                )}
              >
                {reportNotice.kind === "ok" ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-white/50" />
                ) : (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400/70" />
                )}
                <p className="text-xs text-white/50">{reportNotice.message}</p>
              </div>
            )}
          </AdminGlassCard>
        </div>
      )}

      <AdminGlassCard className="flex items-start gap-3 p-4 sm:p-5">
        <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-white/40" />
        <div>
          <p className="text-sm font-medium text-white/65">Datos reales</p>
          <p className="mt-0.5 text-xs leading-relaxed text-white/40">
            Overview = 1 request a{" "}
            <code className="rounded border border-white/[0.08] bg-white/[0.04] px-1 py-0.5 text-[10px] text-white/55">
              /api/admin/dashboard-overview
            </code>{" "}
            (Stripe, funnel_events, intents, image_views, memberships). Refresh cada 30s.
            Revenue de <strong className="text-white/60">producto</strong> (Stripe brand +
            MP cobros del sitio) en{" "}
            <Link to="/admin/revenue" className="underline hover:text-white/70">
              /admin/revenue
            </Link>
            . Finanzas personales (wallet MP) viven en la DB{" "}
            <code className="rounded border border-white/[0.08] bg-white/[0.04] px-1 py-0.5 text-[10px] text-white/55">
              personal-finance
            </code>
            , no en este panel.
          </p>
        </div>
      </AdminGlassCard>
    </AdminPageShell>
  );
}
